<template>
  <div class="reviewers-select">
    <div class="reviewers" v-if="reviewers.length > 0">
      <div
        class="reviewer"
        v-for="(reviewer, i) of reviewers"
        :key="i"
        :data-test="'reviewer-' + reviewer.id"
      >
        <p>#{{ i + 1 }} {{ reviewer.name }}</p>
        <div class="action-buttons">
          <PhCaretUp
            :class="{ disabled: i === 0 }"
            :size="24"
            @click.prevent.stop="moveUp(i)"
            :data-test="'move-up-' + reviewer.id"
          />
          <PhCaretDown
            :class="{ disabled: i === reviewers.length - 1 }"
            :size="24"
            @click.prevent.stop="moveDown(i)"
            :data-test="'move-down-' + reviewer.id"
          />
          <PhX
            class="remove-reviewer"
            :size="20"
            @click.prevent.stop="removeReviewer(reviewer.id)"
            :data-test="'remove-reviewer-' + reviewer.id"
          />
        </div>
      </div>
    </div>
    <div class="add-reviewer">
      <DropDown
        :items="dropdownItems"
        v-model="selectedReviewerToAdd"
        placeholder="Lägg till attesterare"
        :disabled="dropdownItems.length === 0"
        data-test="add-reviewer-dropdown"
      />
      <button
        class="button"
        @click="addReviewer"
        type="button"
        :disabled="dropdownItems.length === 0"
        data-test="add-reviewer-button"
      >
        {{ t('add') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import DropDown from '@/components/common/DropDown.vue';
import { PhCaretDown, PhCaretUp, PhX } from '@phosphor-icons/vue';
import type { models } from 'cesg-shared';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  allReviewers: {
    type: Array as PropType<models.FortnoxUser[]>,
    required: true,
  },
  defaultReviewers: {
    type: Array as PropType<models.FortnoxUserReviewRanking[]>,
    required: false,
    default: () => [],
  },
});
const emit = defineEmits(['reviewersChanged']);

const reviewers = ref<models.FortnoxUser[]>([]);

onMounted(() => {
  if (props.defaultReviewers.length > 0) {
    reviewers.value = props.defaultReviewers
      .map((r) => {
        return props.allReviewers.find((reviewer) => reviewer.id === r.id)!;
      })
      .filter((r): r is models.FortnoxUser => r !== undefined);
  }
});

const dropdownItems = computed(() =>
  props.allReviewers
    .map((reviewer) => ({
      text: reviewer.name,
      value: reviewer.id,
    }))
    .filter(
      (reviewer) => !reviewers.value.find((r) => r.id === reviewer.value),
    ),
);

const selectedReviewerToAdd = ref<string | null>(null);

function addReviewer() {
  if (selectedReviewerToAdd.value) {
    const uniqueReviewers = new Set(reviewers.value);

    uniqueReviewers.add(
      props.allReviewers.find(
        (reviewer) => reviewer.id === selectedReviewerToAdd.value,
      )!,
    );

    reviewers.value = Array.from(uniqueReviewers);

    selectedReviewerToAdd.value = null;
  }
}

function removeReviewer(reviewerId: string) {
  reviewers.value = reviewers.value.filter(
    (reviewer) => reviewer.id !== reviewerId,
  );
}

function moveUp(index: number) {
  if (index > 0) {
    const temp = reviewers.value[index - 1];
    reviewers.value[index - 1] = reviewers.value[index];
    reviewers.value[index] = temp;

    emit(
      'reviewersChanged',
      reviewers.value.map((reviewer, i) => {
        return {
          id: reviewer.id,
          ranking: i,
        } as models.FortnoxUserReviewRanking;
      }),
    );
  }
}

function moveDown(index: number) {
  if (index < reviewers.value.length - 1) {
    const temp = reviewers.value[index + 1];
    reviewers.value[index + 1] = reviewers.value[index];
    reviewers.value[index] = temp;

    emit(
      'reviewersChanged',
      reviewers.value.map((reviewer, i) => {
        return {
          id: reviewer.id,
          ranking: i,
        } as models.FortnoxUserReviewRanking;
      }),
    );
  }
}

watch(reviewers, () => {
  emit(
    'reviewersChanged',
    reviewers.value.map((reviewer, i) => {
      return {
        id: reviewer.id,
        ranking: i,
      } as models.FortnoxUserReviewRanking;
    }),
  );
});
</script>

<style lang="scss" scoped>
.reviewers-select {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  margin-top: 20px;

  .reviewers {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 20px;

    .reviewer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;

      width: 15rem;

      p {
        margin: 0;
        text-overflow: ellipsis;
        overflow: hidden;
        text-wrap: nowrap;
      }

      .action-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;

        & > * {
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            background-color: rgba(136, 159, 181, 0.4);
          }

          background-color: rgba(136, 159, 181, 0.2);
          border-radius: 50%;
          color: #303030;

          padding: 2px;

          &.disabled {
            cursor: not-allowed;
            opacity: 0.3;
          }
        }

        .remove-reviewer {
          margin-left: 1rem;
          background-color: var(--table-red);
          padding: 4px;

          &:hover {
            opacity: 0.8;
          }
        }
      }
    }

    margin-bottom: 20px;
  }

  .add-reviewer {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;

    width: 15rem;

    .button {
      width: 5rem;
    }
  }
}
</style>
